import React, { forwardRef, useImperativeHandle } from "react";
import { useFormik } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Stack, Button } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DatePickerInput from "../Inputs/DatePicker";

dayjs.extend(customParseFormat);

const makeInitial = (filter) => ({
  start_date: filter.start_date ? dayjs(filter.start_date, "YYYY-MM-DD") : null,
  end_date: filter.end_date ? dayjs(filter.end_date, "YYYY-MM-DD") : null,
});

const ChartFilter = forwardRef(({ filter, changeFilterHandle }, ref) => {
  const formik = useFormik({
    initialValues: makeInitial(filter),
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      
      if (values.start_date && values.end_date) {
        if (values.end_date.isBefore(values.start_date, 'day')) {
          errors.end_date = "End date cannot be before start date";
        }
      }
      
      return errors;
    },
    onSubmit: (values) => {
      changeFilterHandle({
        start_date: values.start_date?.format("YYYY-MM-DD") ?? null,
        end_date: values.end_date?.format("YYYY-MM-DD") ?? null,
      });
    },
  });

  const clearAllFilters = () => {
    formik.resetForm({
      values: {
        start_date: null,
        end_date: null,
      },
    });
    
    // Automatically submit cleared filters
    changeFilterHandle({
      start_date: null,
      end_date: null,
    });
  };

  // Expose submit method to parent component via ref
  useImperativeHandle(ref, () => ({
    submit: formik.handleSubmit,
    reset: clearAllFilters,
    values: formik.values,
  }));

  const hasActiveFilters = formik.values.start_date || formik.values.end_date;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2}>
        {hasActiveFilters && (
          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <Button 
              size="small" 
              onClick={clearAllFilters} 
              color="secondary"
              variant="outlined"
            >
              Clear Filters
            </Button>
          </Stack>
        )}
        
        <Stack gap={3} component="form" onSubmit={formik.handleSubmit}>
          <DatePickerInput
            label="Start Date"
            dateValue={formik.values.start_date}
            onDateChange={(d) => formik.setFieldValue("start_date", d)}
            maxDate={formik.values.end_date}
            error={Boolean(formik.errors.start_date)}
            helperText={formik.errors.start_date}
          />
          
          <DatePickerInput
            label="End Date"
            dateValue={formik.values.end_date}
            onDateChange={(d) => formik.setFieldValue("end_date", d)}
            minDate={formik.values.start_date}
            error={Boolean(formik.errors.end_date)}
            helperText={formik.errors.end_date}
          />
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
});

ChartFilter.displayName = 'ChartFilter';

export default ChartFilter;